import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bike, Loader2, CheckCircle2, Camera, AlertTriangle, LogOut, IndianRupee } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
const KEY = "fleetos_rider_token";

export default function RiderPortal() {
  useDocumentTitle("Rider portal");
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const [phone, setPhone] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [photoId, setPhotoId] = useState(null);

  const load = (t) => axios.get(`${API}/delivery/me`, { headers: { Authorization: `Bearer ${t}` } })
    .then(({ data }) => setData(data))
    .catch(() => { localStorage.removeItem(KEY); setToken(null); });

  useEffect(() => { if (token) load(token); }, [token]);

  const login = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/delivery/auth/login`, { phone, password: pw });
      localStorage.setItem(KEY, data.token); setToken(data.token);
    } catch (e) { setErr(e.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };
  const logout = () => { localStorage.removeItem(KEY); setToken(null); setData(null); };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f || !data?.vehicle?.qr_token) return;
    const fd = new FormData(); fd.append("file", f); fd.append("category", "daily");
    const { data: up } = await axios.post(`${API}/delivery/public/upload/${data.vehicle.qr_token}`, fd);
    setPhotoId(up.id);
  };
  const checkin = async () => {
    setBusy(true);
    try { await axios.post(`${API}/delivery/me/checkin`, { photo_file: photoId }, { headers: { Authorization: `Bearer ${token}` } }); await load(token); }
    finally { setBusy(false); }
  };

  if (!token) {
    return (
      <Centered>
        <div data-testid="rider-login" className="w-full max-w-sm rounded-[24px] border border-tenant-border bg-tenant-surface p-6 shadow-tenant-float">
          <div className="flex items-center gap-2 text-tenant-primary"><Bike className="h-5 w-5" /><span className="font-bold">Rider portal</span></div>
          <p className="mt-1 text-sm text-muted-foreground">Sign in with your registered phone.</p>
          <div className="mt-5 space-y-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="rider-login-phone" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input data-testid="rider-login-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
            {err && <p className="flex items-center gap-1 text-sm text-status-recovery"><AlertTriangle className="h-4 w-4" /> {err}</p>}
            <Button data-testid="rider-login-submit" onClick={login} disabled={busy} className="w-full gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</Button>
          </div>
        </div>
      </Centered>
    );
  }

  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;
  const b = data.billing, t = data.today;

  return (
    <div className="mx-auto min-h-screen max-w-md space-y-4 bg-tenant-bg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-tenant-primary"><Bike className="h-5 w-5" /><span className="font-bold">{data.rider.name}</span></div>
        <Button data-testid="rider-logout" variant="ghost" size="sm" onClick={logout} className="gap-1"><LogOut className="h-4 w-4" /> Exit</Button>
      </div>

      <div data-testid="rider-today" className={`rounded-[20px] border p-5 shadow-tenant-soft ${t.checked_in ? "border-status-available/40 bg-status-available/5" : "border-status-overdue/40 bg-status-overdue/5"}`}>
        {t.checked_in ? (
          <div className="text-center"><CheckCircle2 className="mx-auto h-10 w-10 text-status-available" /><p className="mt-2 font-semibold">Checked in for today</p></div>
        ) : (
          <div className="text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-status-overdue" />
            <p className="mt-2 font-semibold">Not checked in today</p>
            <p className="mt-1 text-xs text-muted-foreground">{t.warning}</p>
            <label data-testid="rider-photo" className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-tenant-button border border-dashed border-tenant-border py-3 text-sm text-muted-foreground">
              <Camera className="h-4 w-4" /> {photoId ? "Photo added ✓" : "Photo with vehicle"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
            </label>
            <Button data-testid="rider-checkin-submit" onClick={checkin} disabled={busy} className="mt-3 w-full gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Check in now</Button>
          </div>
        )}
      </div>

      <div className="rounded-[20px] border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <p className="text-sm text-muted-foreground">{t.plan_label}</p>
        {data.vehicle && <p className="mt-1 font-semibold">{data.vehicle.reg_number} · {data.vehicle.model}</p>}
        <div className="mt-3 flex items-center justify-between">
          <span className="flex items-center gap-1 text-sm text-muted-foreground"><IndianRupee className="h-4 w-4" /> Charges so far</span>
          <span className="text-lg font-bold">{inr(b.total)}</span>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-tenant-button bg-tenant-bg py-2"><p className="font-bold">{b.days_held}</p><p className="text-muted-foreground">days</p></div>
          <div className="rounded-tenant-button bg-tenant-bg py-2"><p className="font-bold text-status-available">{b.checkin_days}</p><p className="text-muted-foreground">check-ins</p></div>
          <div className="rounded-tenant-button bg-tenant-bg py-2"><p className={`font-bold ${b.idle_days ? "text-status-overdue" : ""}`}>{b.idle_days}</p><p className="text-muted-foreground">idle</p></div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-tenant-bg p-4">{children}</div>;
}
